import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Grid,
  Popover,
  Skeleton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { setselectedDate, setselectedTime } from "../../store/slice/Salon.slice";

const DateComponent = ({ isOpen, handleClose, Ref, loader }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const dispatch = useDispatch();
  const { css } = useSelector((store) => store?.widgetSettings);
  const dateCss = css?.find(
    (item) => item?.cssTabFieldsGroupName === "Salon Date"
  )?.fields;

  const [anchorOriginVert, setAnchorOriginVert] = useState("bottom");
  const [transformOriginVert, setTransformOriginVert] = useState("top");

  useEffect(() => {
    if (!isOpen || !Ref?.current) return;
    const rect = Ref.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    if (spaceBelow < 380) {
      setAnchorOriginVert("top");
      setTransformOriginVert("bottom");
    } else {
      setAnchorOriginVert("bottom");
      setTransformOriginVert("top");
    }
  }, [isOpen, Ref]);

  const { availabilityDate, languageWords } = useSelector(
    (store) => store?.salon
  );

  const today = dayjs();
  const [currentDate, setCurrentDate] = useState(today);
  const [selectedDate, setSelectedDate] = useState(null);

  const daysOfWeek = [
    languageWords?.monday?.slice(0, 2)     || "Mo",
    languageWords?.Tuesday?.slice(0, 2)    || "Tu",
    languageWords?.Wednesday?.slice(0, 2)  || "We",
    languageWords?.Thursday?.slice(0, 2)   || "Th",
    languageWords?.Friday?.slice(0, 2)     || "Fr",
    languageWords?.Saturday?.slice(0, 2)   || "Sa",
    languageWords?.Sunday?.slice(0, 2)     || "Su",
  ];

  const daysInMonth     = currentDate.daysInMonth();
  const firstDayOfMonth = (currentDate.startOf("month").day() + 6) % 7;

  const isAvailableDate = (day) =>
    availabilityDate?.includes(currentDate.date(day).format("YYYY-MM-DD"));

  const isSelected = (day) =>
    selectedDate && selectedDate.isSame(currentDate.date(day), "day");

  const handlePrevMonth = () => setCurrentDate(currentDate.subtract(1, "month"));
  const handleNextMonth = () => setCurrentDate(currentDate.add(1, "month"));

  const handleDateSelect = (day) => {
    const selected = currentDate.date(day);
    dispatch(setselectedTime(""));
    setSelectedDate(selected);
    dispatch(setselectedDate(selected.format("DD-MM-YYYY")));
    handleClose();
  };

  const getTranslatedMonth = () => {
    const monthName = currentDate.format("MMMM");
    return languageWords?.[monthName] || monthName;
  };

  // Responsive calendar cell size
  const cellSize = isMobile ? 36 : isTablet ? 38 : 40;
  const cellFontSize = isMobile ? "0.8125rem" : "0.9375rem";

  return (
    <Popover
      open={isOpen}
      anchorEl={Ref?.current}
      onClose={handleClose}
      anchorOrigin={{ vertical: anchorOriginVert, horizontal: "center" }}
      transformOrigin={{ vertical: transformOriginVert, horizontal: "center" }}
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 12px 40px rgba(0,0,0,0.14)",
          overflow: "hidden",
          mt: 0.5,
          // Fluid: fills most of mobile screen, capped on desktop
          width: isMobile ? "min(96vw, 340px)" : "clamp(300px, 28vw, 400px)",
          backgroundColor: dateCss?.salonCalendarBackgroundColor || "#fff",
        },
      }}
    >
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>

        {/* ── Year nav ── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 0.5,
          }}
        >
          <IconButton
            onClick={handlePrevMonth}
            disabled={loader}
            size={isMobile ? "small" : "medium"}
            sx={{
              color: dateCss?.salonCalendarArrowColor || "#666",
              "&:hover": { color: dateCss?.salonCalendarArrowColorHover || "#A57939" },
              // 40px min touch target
              minWidth: { xs: 40, sm: "auto" },
              minHeight: { xs: 40, sm: "auto" },
            }}
          >
            <ArrowBack fontSize={isMobile ? "small" : "medium"} />
          </IconButton>

          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: "0.9375rem", sm: "1.0625rem" },
              color: dateCss?.salonCalendarYearTextColor || "#222",
              letterSpacing: "0.03em",
            }}
          >
            {currentDate.format("YYYY")}
          </Typography>

          <IconButton
            onClick={handleNextMonth}
            disabled={loader}
            size={isMobile ? "small" : "medium"}
            sx={{
              color: dateCss?.salonCalendarArrowColor || "#666",
              "&:hover": { color: dateCss?.salonCalendarArrowColorHover || "#A57939" },
              minWidth: { xs: 40, sm: "auto" },
              minHeight: { xs: 40, sm: "auto" },
            }}
          >
            <ArrowForward fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        </Box>

        {/* ── Month name ── */}
        <Typography
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            color: dateCss?.salonCalendarMonthTextColor || "#333",
            mb: { xs: 1.5, sm: 2 },
            letterSpacing: "-0.01em",
          }}
        >
          {getTranslatedMonth()}
        </Typography>

        {/* ── Day-of-week headers ── */}
        <Grid container columns={7} sx={{ mb: 0.5 }}>
          {daysOfWeek.map((day, idx) => (
            <Grid item xs={1} key={idx}>
              <Typography
                sx={{
                  textAlign: "center",
                  fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                  fontWeight: 600,
                  color: dateCss?.salonCalendarDaysTextColor || "#999",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  py: 0.5,
                }}
              >
                {day}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {/* ── Calendar grid ── */}
        {loader ? (
          <Grid container columns={7} spacing={0.5}>
            {Array.from({ length: firstDayOfMonth + daysInMonth }).map((_, idx) => (
              <Grid item xs={1} key={idx}>
                <Skeleton
                  variant="rounded"
                  sx={{
                    width: "100%",
                    // Aspect-ratio 1:1 via paddingBottom trick
                    height: 0,
                    paddingBottom: "100%",
                    borderRadius: "8px",
                  }}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container columns={7} spacing={{ xs: 0.375, sm: 0.5 }}>
            {/* Empty cells for first-day offset */}
            {[...Array(firstDayOfMonth)].map((_, i) => (
              <Grid item xs={1} key={`empty-${i}`} />
            ))}

            {/* Date cells */}
            {[...Array(daysInMonth)].map((_, dayIndex) => {
              const day      = dayIndex + 1;
              const available = isAvailableDate(day);
              const selected  = isSelected(day);

              return (
                <Grid item xs={1} key={dayIndex}>
                  <Box
                    onClick={() => available && handleDateSelect(day)}
                    sx={{
                      position: "relative",
                      // Fluid square: use aspect-ratio box via padding trick
                      width: "100%",
                      paddingBottom: "100%",
                      borderRadius: { xs: "6px", sm: "8px" },
                      cursor: available ? "pointer" : "not-allowed",
                      border: "2px",
                      borderStyle: "dotted",
                      borderColor: available
                        ? theme?.widget?.calendarDateAvailableBorder || "#A57939"
                        : dateCss?.salonCalendarDateBorderColor || "#e0e0e0",
                      backgroundColor: selected
                        ? dateCss?.salonCalendarDateBackgroundColorActive || "#A57939"
                        : available
                        ? dateCss?.salonCalendarDateBackgroundColor || "#fff"
                        : dateCss?.saloncalendarDateDisabledBg || "#f5f5f5",
                      transition: "all 0.15s ease",
                      "&:hover": available
                        ? {
                            transform: "scale(1.08)",
                            boxShadow: "0 4px 12px rgba(165,121,57,0.2)",
                            zIndex: 1,
                          }
                        : {},
                    }}
                  >
                    {/* Absolutely centred number inside the padding-based square */}
                    <Typography
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.9375rem" },
                        fontWeight: available || selected ? 700 : 400,
                        lineHeight: 1,
                        color: selected
                          ? dateCss?.salonCalendarDateTextColorActive || "#fff"
                          : available
                          ? dateCss?.salonCalendarDateTextColor || "#222"
                          : dateCss?.saloncalendarDateDisabledText || "#ccc",
                      }}
                    >
                      {day}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </Popover>
  );
};

export default DateComponent;

